import { useState, useEffect, useCallback } from "react";
import api, { formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Loader2, Package, ArrowUpDown } from "lucide-react";
import { toast } from "sonner";

export default function StockAdjustmentsPage() {
  const [adjustments, setAdjustments] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({ product_id: "", adjustment_type: "add", quantity: 0, reason: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/stock-adjustments?limit=100"),
      api.get("/products?limit=500"),
    ]).then(([adj, prod]) => {
      setAdjustments(adj.data.adjustments || []);
      setProducts(prod.data.products || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.product_id) return toast.error("Select a product");
    if (!form.quantity || form.quantity <= 0) return toast.error("Enter a valid quantity");
    setSaving(true);
    try {
      await api.post("/stock-adjustments", form);
      toast.success("Stock adjusted"); setDialog(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="stock-adjustments-page">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-heading font-bold text-2xl text-[#1F2937]">Stock Adjustments</h1>
          <p className="text-sm text-gray-500 mt-1">Adjust product stock levels</p></div>
        <Button data-testid="new-adjustment-btn" onClick={() => { setForm({ product_id: "", adjustment_type: "add", quantity: 0, reason: "" }); setDialog(true); }} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm"><Plus className="w-4 h-4 mr-1.5" />New Adjustment</Button>
      </div>
      {loading ? <div className="animate-pulse h-40 bg-gray-100 rounded-sm" /> : adjustments.length === 0 ? (
        <Card className="border rounded-sm"><CardContent className="p-12 text-center text-gray-400"><ArrowUpDown className="w-10 h-10 mx-auto mb-3 opacity-40" /><p>No stock adjustments yet</p></CardContent></Card>
      ) : (
        <Card className="border rounded-sm shadow-sm"><div className="overflow-x-auto">
          <table data-testid="adjustments-table" className="w-full text-sm">
            <thead><tr className="border-b bg-gray-50/50">
              <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Date</th>
              <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Product</th>
              <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Type</th>
              <th className="p-3 text-right text-xs font-semibold uppercase text-gray-500">Qty</th>
              <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Reason</th>
              <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">By</th>
            </tr></thead>
            <tbody>{adjustments.map((a, i) => (
              <tr key={i} className="border-b border-gray-100">
                <td className="p-3 text-gray-600">{formatDate(a.created_at)}</td>
                <td className="p-3 font-medium">{a.product_name}</td>
                <td className="p-3"><Badge className={`rounded-full text-xs border ${a.adjustment_type === "add" ? "bg-green-50 text-green-700 border-green-200" : "bg-red-50 text-red-700 border-red-200"}`}>{a.adjustment_type === "add" ? "Added" : "Removed"}</Badge></td>
                <td className="p-3 text-right font-semibold">{a.adjustment_type === "add" ? "+" : "-"}{a.quantity}</td>
                <td className="p-3 text-gray-600">{a.reason || "-"}</td>
                <td className="p-3 text-gray-500">{a.created_by_name || "-"}</td>
              </tr>
            ))}</tbody>
          </table>
        </div></Card>
      )}
      <Dialog open={dialog} onOpenChange={setDialog}><DialogContent>
        <DialogHeader><DialogTitle>New Stock Adjustment</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><label className="text-xs font-semibold text-gray-500 uppercase block mb-1">Product</label>
            <Select value={form.product_id} onValueChange={v => setForm({...form, product_id: v})}>
              <SelectTrigger data-testid="adj-product"><SelectValue placeholder="Select..." /></SelectTrigger>
              <SelectContent>{products.map(p => <SelectItem key={p.product_id} value={p.product_id}>{p.sku} - {p.product_name} (Stock: {p.current_stock || 0})</SelectItem>)}</SelectContent>
            </Select></div>
          <div><label className="text-xs font-semibold text-gray-500 uppercase block mb-1">Type</label>
            <Select value={form.adjustment_type} onValueChange={v => setForm({...form, adjustment_type: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="add">Add Stock</SelectItem><SelectItem value="remove">Remove Stock</SelectItem></SelectContent>
            </Select></div>
          <div><label className="text-xs font-semibold text-gray-500 uppercase block mb-1">Quantity</label>
            <Input data-testid="adj-qty" type="number" value={form.quantity} onChange={e => setForm({...form, quantity: parseInt(e.target.value) || 0})} /></div>
          <div><label className="text-xs font-semibold text-gray-500 uppercase block mb-1">Reason</label>
            <Input value={form.reason} onChange={e => setForm({...form, reason: e.target.value})} placeholder="e.g. Physical count, Damaged goods" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button>
          <Button data-testid="save-adj-btn" onClick={save} disabled={saving} className="bg-[#E63946] hover:bg-[#C1121F] text-white">{saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}Save Adjustment</Button>
        </DialogFooter>
      </DialogContent></Dialog>
    </div>
  );
}
